import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useModal } from '../context/ModalContext';
import { MapPin, Phone, Mail, Clock, ExternalLink } from 'lucide-react';

const ContactSection = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { openModal } = useModal();
  const isRtl = language === 'ar';

  const phoneNumber = t('contact.phone.number');
  const emailAddress = t('contact.email.address');
  const officeAddress = t('contact.office.address');
  const officeCity = t('contact.office.city');
  const mapHref = `https://maps.google.com/?q=${encodeURIComponent([officeAddress, officeCity].filter(Boolean).join(', '))}`;

  const contactItems = [
    { key: 'office', icon: MapPin, title: t('contact.office.title'), lines: [officeAddress, officeCity] },
    { key: 'phone', icon: Phone, title: t('contact.phone.title'), lines: [phoneNumber], link: `tel:${phoneNumber.replace(/[^+0-9]/g, '')}` },
    { key: 'email', icon: Mail, title: t('contact.email.title'), lines: [emailAddress], link: `mailto:${emailAddress}` },
    { key: 'hours', icon: Clock, title: t('contact.hours.title'), lines: [t('contact.hours.weekdays'), t('contact.hours.weekend')] },
  ];

  return (
    <section id="contact" data-testid="contact-section" className={`py-20 sm:py-28 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <p className="text-[var(--gold-primary)] uppercase tracking-[0.2em] text-xs font-semibold mb-4" data-testid="contact-overline">{t('contact.overline')}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 tracking-tight" data-testid="contact-title">
            <span className="gold-text">{t('contact.title')}</span>
          </h2>
          <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-testid="contact-subtitle">{t('contact.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-6">
            {contactItems.map(item => (
              <div
                key={item.key}
                className={`group p-6 sm:p-8 rounded-sm transition-all duration-300 cursor-pointer ${isRtl ? 'text-right' : 'text-left'} ${isDark ? 'bg-[#111111] border border-[var(--border-color)] hover:border-[var(--gold-primary)]/50' : 'bg-gray-50 border border-gray-200 hover:border-[var(--gold-primary)]/50'}`}
                data-testid={`contact-${item.key}`}
                onClick={() => openModal('contact', { tab: item.key })}
                role="button" tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && openModal('contact', { tab: item.key })}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-sm mb-5 transition-colors ${isDark ? 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20' : 'bg-[var(--gold-primary)]/10 group-hover:bg-[var(--gold-primary)]/20'}`}>
                  <item.icon className="w-6 h-6 text-[var(--gold-primary)]" />
                </div>
                <h3 className={`font-display text-lg font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>{item.title}</h3>
                <div className="space-y-1">
                  {item.lines.map((line, index) => (
                    <p key={index} className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                      {item.link && index === 0 ? (
                        <a href={item.link} dir={item.key === 'phone' || item.key === 'email' ? 'ltr' : undefined} className={`hover:text-[var(--gold-primary)] transition-colors inline-flex items-center gap-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                          {line}<ExternalLink className="w-3 h-3 opacity-50 rtl-flip" />
                        </a>
                      ) : line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className={`h-full min-h-[300px] lg:min-h-0 rounded-sm overflow-hidden relative ${isDark ? 'bg-[#111111] border border-[var(--border-color)]' : 'bg-gray-100 border border-gray-200'}`}>
              <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url('/uploads/9a42e3f1-cacf-4315-89d5-58f9a9d4bdcb.png')` }} />
              <div className={`absolute inset-0 ${isDark ? 'bg-[#111111]/80' : 'bg-white/80'}`} />
              <div className="relative h-full flex flex-col items-center justify-center p-8 text-center">
                <img src="/uploads/f0ff146b-d9ae-4c6c-b8cb-08a79738ee50.png" alt="Royal Tobacco" className="h-20 sm:h-24 w-auto mb-6 opacity-80" />
                <div className="mb-6">
                  <p className={`font-display text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>{t('contact.office.title')}</p>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{officeAddress}</p>
                  <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{officeCity}</p>
                </div>
                <a href={mapHref} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--gold-primary)] text-black font-semibold text-xs uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}>
                  <MapPin className="w-4 h-4" />
                  {language === 'ar' ? 'عرض على الخريطة' : 'View on Map'}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className={`mt-16 p-8 sm:p-10 rounded-sm text-center ${isDark ? 'bg-gradient-to-r from-[var(--gold-primary)]/10 via-[var(--gold-primary)]/5 to-[var(--gold-primary)]/10 border border-[var(--gold-primary)]/20' : 'bg-gradient-to-r from-[var(--gold-primary)]/5 via-[var(--gold-primary)]/10 to-[var(--gold-primary)]/5 border border-[var(--gold-primary)]/20'}`}>
          <h3 className={`font-display text-xl sm:text-2xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-800'}`}>{language === 'ar' ? 'هل لديك استفسار؟' : 'Have a Question?'}</h3>
          <p className={`text-sm sm:text-base mb-6 max-w-xl mx-auto ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {language === 'ar' ? 'فريقنا جاهز للإجابة على جميع استفساراتكم. تواصلوا معنا اليوم!' : 'Our team is ready to answer all your inquiries. Get in touch with us today!'}
          </p>
          <div className={`flex flex-wrap items-center justify-center gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <a href={`tel:${phoneNumber.replace(/[^+0-9]/g, '')}`} className={`inline-flex items-center gap-2 px-6 py-3 bg-[var(--gold-primary)] text-black font-semibold text-sm uppercase tracking-wider rounded-sm hover:bg-[var(--gold-hover)] transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Phone className="w-4 h-4" />{language === 'ar' ? 'اتصل بنا' : 'Call Us'}
            </a>
            <a href={`mailto:${emailAddress}`} className={`inline-flex items-center gap-2 px-6 py-3 border-2 border-[var(--gold-primary)] font-semibold text-sm uppercase tracking-wider rounded-sm transition-colors text-[var(--gold-primary)] hover:bg-[var(--gold-primary)] hover:text-black ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Mail className="w-4 h-4" />{language === 'ar' ? 'راسلنا' : 'Email Us'}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
